import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'src/redux/store';

import { ITrackCount, ITrackOrder, ITrackParams, ITrackTab } from './track.interface';

export type { ITrackCarrier, ITrackSchedule, ITrackAddress, ITrackOrder, ITrackParams, ITrackCount, ITrackTab } from './track.interface';

interface ITrackState {
    trackList: ITrackOrder[];
    /** Server-side total for the current filters (API pagination.totalRecords) — drives the pager. */
    trackTotalRecords: number;
    trackParams: ITrackParams;
    trackCount: ITrackCount;
    isLoading: boolean;
    /** Separate flag for overview stats skeleton — not set when only filterBySubStatus changes. */
    isOverviewLoading: boolean;
    trackOrderDetail: any | null;
    isTrackDetailLoading: boolean;
    selectedLeg: any | null;
    selectedLegIndex: number;
    fullRouteDistance: number;
    /** Miles from the truck's current position to the consignee (LTL card). */
    remainingDistance: number;
    activeScheduleSection: 'pickup' | 'inTransit' | 'delivery' | null;
    /** Resolved 3-tier portal visibility returned by the listRadarOrders API. */
    portalVisibility: Record<string, boolean> | null;
    // ── Saved views (shared by the table view tabs and the map card dropdown) ──
    /** Default stage tabs followed by the user's saved views (custom_views, tableAlias cp_track). */
    tabsData: ITrackTab[];
    /** value of the active tab: a stage ('dispatched' | 'in-transit' | 'delivered') or a saved view id as string. */
    activeViewTab: string;
    /** True once the saved-view list has been fetched this session — guards against refetching on every mount. */
    isFetchedTabs: boolean;
}

export const initialTrackCount: ITrackCount = {
    pickups: { total: 0, completed: 0, pending: 0 },
    inTransit: { total: 0, onTrack: 0, delayed: 0 },
    deliveries: { total: 0, completed: 0, pending: 0 },
};

const initialTrackParams: ITrackParams = {
    status: 'dispatched',
    serviceType: 'all',
    sortField: 'lastUpdatedAt',
    sortType: 'desc',
    startDate: '',
    endDate: '',
    billingAccountId: null,
    divisionId: null,
    search: '',
    page: 1,
    limit: 25,
    refSearch: '',
    shipperId: null,
    consigneeId: null,
    originSearch: '',
    destinationSearch: '',
    filterBySubStatus: '',
};

const initialState: ITrackState = {
    trackList: [],
    trackTotalRecords: 0,
    trackParams: initialTrackParams,
    trackCount: initialTrackCount,
    isLoading: true,
    isOverviewLoading: true,
    trackOrderDetail: null,
    isTrackDetailLoading: false,
    selectedLeg: null,
    selectedLegIndex: 0,
    fullRouteDistance: 0,
    remainingDistance: 0,
    portalVisibility: null,
    activeScheduleSection: null,
    tabsData: [],
    activeViewTab: initialTrackParams.status,
    isFetchedTabs: false,
};

export const TrackSlice = createSlice({
    name: 'track',
    initialState,
    reducers: {
        setTrackList: (state, action: PayloadAction<ITrackOrder[]>) => {
            state.trackList = action.payload;
        },
        setTrackTotalRecords: (state, action: PayloadAction<number>) => {
            state.trackTotalRecords = action.payload;
        },
        setTrackParams: (state, action: PayloadAction<Partial<ITrackParams>>) => {
            // Re-picking the value that is already applied (e.g. selecting FTL again in the
            // service dropdown) used to spread into a brand-new object every time. That changed
            // the trackParams reference, and the list/overview effect in Track/index.tsx keys off
            // that reference — so an identical selection re-fired the whole listTracks request.
            // Bail out when the patch changes nothing so the reference (and the effect) stay put.
            // Every ITrackParams value is a primitive, so a per-key Object.is check is enough.
            const patch = Object.entries(action.payload) as [keyof ITrackParams, ITrackParams[keyof ITrackParams]][];
            const hasChange = patch.some(([key, value]) => !Object.is(state.trackParams[key], value));

            if (!hasChange) return;

            state.trackParams = { ...state.trackParams, ...action.payload };
        },
        setTrackCount: (state, action: PayloadAction<ITrackCount>) => {
            state.trackCount = action.payload;
        },
        setTrackLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setOverviewLoading: (state, action: PayloadAction<boolean>) => {
            state.isOverviewLoading = action.payload;
        },
        updateTrackOrder: (state, action: PayloadAction<{ masterOrderFullId: string; data: Partial<ITrackOrder> }>) => {
            // Order-level patch (priority, notifications, …) — with Show-as-Multi-Leg ON one
            // order renders as several leg cards, so EVERY row of that order must update,
            // not just the first match.
            state.trackList.forEach((order, index) => {
                if (order.masterOrderFullId === action.payload.masterOrderFullId) {
                    state.trackList[index] = { ...state.trackList[index], ...action.payload.data };
                }
            });
        },
        setTrackOrderDetail: (state, action: PayloadAction<any>) => {
            state.trackOrderDetail = action.payload;
        },
        setTrackDetailLoading: (state, action: PayloadAction<boolean>) => {
            state.isTrackDetailLoading = action.payload;
        },
        setSelectedLeg: (state, action: PayloadAction<{ leg: any; index: number }>) => {
            state.selectedLeg = action.payload.leg;
            state.selectedLegIndex = action.payload.index;
        },
        setFullRouteDistance: (state, action: PayloadAction<number>) => {
            state.fullRouteDistance = action.payload;
        },
        setRemainingDistance: (state, action: PayloadAction<number>) => {
            state.remainingDistance = action.payload;
        },
        setActiveScheduleSection: (state, action: PayloadAction<'pickup' | 'inTransit' | 'delivery' | null>) => {
            state.activeScheduleSection = action.payload;
        },
        setTrackPortalVisibility: (state, action: PayloadAction<Record<string, boolean> | null>) => {
            state.portalVisibility = action.payload;
        },
        setTrackTabsData: (state, action: PayloadAction<ITrackTab[]>) => {
            state.tabsData = action.payload;
        },
        setTrackActiveViewTab: (state, action: PayloadAction<string>) => {
            state.activeViewTab = action.payload;
        },
        setTrackIsFetchedTabs: (state, action: PayloadAction<boolean>) => {
            state.isFetchedTabs = action.payload;
        },
        clearTrackOrderDetail: (state) => {
            state.trackOrderDetail = null;
            state.isTrackDetailLoading = false;
            state.selectedLeg = null;
            state.selectedLegIndex = 0;
            state.fullRouteDistance = 0;
            state.remainingDistance = 0;
            state.activeScheduleSection = null;
        },
    },
});

export default TrackSlice.reducer;

export const {
    setTrackList,
    setTrackTotalRecords,
    setTrackParams,
    setTrackCount,
    setTrackLoading,
    setOverviewLoading,
    updateTrackOrder,
    setTrackOrderDetail,
    setTrackDetailLoading,
    clearTrackOrderDetail,
    setSelectedLeg,
    setFullRouteDistance,
    setRemainingDistance,
    setTrackPortalVisibility,
    setActiveScheduleSection,
    setTrackTabsData,
    setTrackActiveViewTab,
    setTrackIsFetchedTabs,
} = TrackSlice.actions;

export const trackList = (state: RootState) => state.track.trackList;
export const trackTotalRecords = (state: RootState) => state.track.trackTotalRecords;
export const trackParams = (state: RootState) => state.track.trackParams;
export const trackCount = (state: RootState) => state.track.trackCount;
export const trackLoading = (state: RootState) => state.track.isLoading;
export const trackOverviewLoading = (state: RootState) => state.track.isOverviewLoading;
export const trackOrderDetail = (state: RootState) => state.track.trackOrderDetail;
export const trackDetailLoading = (state: RootState) => state.track.isTrackDetailLoading;
export const selectSelectedLeg = (state: RootState) => state.track.selectedLeg;
export const selectSelectedLegIndex = (state: RootState) => state.track.selectedLegIndex;
export const selectFullRouteDistance = (state: RootState) => state.track.fullRouteDistance;
export const selectRemainingDistance = (state: RootState) => state.track.remainingDistance;
export const selectActiveScheduleSection = (state: RootState) => state.track.activeScheduleSection;
export const trackTabsData = (state: RootState) => state.track.tabsData;
export const trackActiveViewTab = (state: RootState) => state.track.activeViewTab;
export const trackIsFetchedTabs = (state: RootState) => state.track.isFetchedTabs;
